/**
 * Connect flow, step 2 — design_handoff_integrations_page README §7. Where
 * the OAuth redirect lands, one hop after IntegrationAccessCheckPage (the
 * backend hard-redirects to /integrations/check-access/:type/:uuid —
 * cake-app/src/Controller/IntegrationsController.php:72 — so that route
 * can't be renamed; this page carries the uuid as a query param instead).
 *
 * settings.calendars only gets populated by a sync run (see
 * cake-app/src/Utility/Google/GoogleCalendars.php), not by OAuth alone, so
 * this page triggers one on mount — same as the legacy SelectCalendarsPage.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import Card from "../theme/untitled/Card/Card";
import Button from "../theme/untitled/Button/Button";
import LoadingIndicator from "../../calendar/components/LoadingIndicator";
import CalendarPickerList from "./IntegrationsPage/CalendarPickerList";
import ApiError from "../component/ApiError";
import { useIntegrationStore } from "../../../hooks/redux/integration";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import useQuery from "../../../hooks/useQuery";
import appConfig from "../../../appConfig";
import { INTEGRATION_NAMES } from "../../../consts";
import styles from "./IntegrationConnectPage.module.scss";

const IntegrationConnectCalendarsPage = () => {
  const { provider } = useParams();
  const query = useQuery();
  const uuid = query.get("uuid");
  const history = useHistory();
  const {
    integration,
    setIntegrationModel,
    updateIntegration,
    syncIntegration,
  } = useIntegrationStore();
  const { integrations } = useIntegrationsStore();
  const [selected, setSelected] = useState({});
  const hasTriggeredSync = useRef(false);

  const model = integration.model?.uuid === uuid ? integration.model : null;

  useEffect(() => {
    if (uuid && !model && integrations.fetchSuccess) {
      const found = integrations.collection.find((i) => i.uuid === uuid);
      if (found) {
        setIntegrationModel(found);
      }
    }
  }, [uuid, model, integrations.fetchSuccess, integrations.collection]);

  useEffect(() => {
    if (uuid && !hasTriggeredSync.current) {
      hasTriggeredSync.current = true;
      syncIntegration(uuid);
    }
  }, [uuid]);

  useEffect(() => {
    if (model?.settings?.calendars) {
      setSelected((prev) => {
        const next = {};
        model.settings.calendars.forEach((c) => {
          next[c.id] = c.id in prev ? prev[c.id] : Boolean(c.sync);
        });
        return next;
      });
    }
  }, [model?.settings?.calendars]);

  // Saving pushes back to /integrations; that push is the signal step 2
  // is done (rather than a route the user can linger on after saving).
  useEffect(() => {
    if (integration.updateSuccess && integration.model?.uuid === uuid) {
      syncIntegration(uuid);
      history.push("/integrations");
    }
  }, [integration.updateSuccess]);

  const calendars = useMemo(() => model?.settings?.calendars || [], [model?.settings?.calendars]);
  const isReadingCalendars = integrations.sync && !calendars.length;
  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const onChange = useCallback(
    (id, checked) => setSelected((prev) => ({ ...prev, [id]: checked })),
    []
  );
  const onSelectAll = useCallback(
    (checked) => {
      const next = {};
      calendars.forEach((c) => {
        next[c.id] = checked;
      });
      setSelected(next);
    },
    [calendars]
  );

  const onSave = useCallback(() => {
    const updatedCalendars = calendars.map((c) => ({ ...c, sync: Boolean(selected[c.id]) }));
    updateIntegration(uuid, {
      ...model,
      settings: { ...model.settings, calendars: updatedCalendars },
    });
  }, [calendars, selected, updateIntegration, uuid, model]);

  // "Cancel" here mirrors the legacy "Skip for now" link, not a hard
  // rollback: the connection already exists and was already authorized at
  // the provider by this point, so leaving without saving lands it in
  // "Setup incomplete" (recoverable from the row later) rather than
  // deleting a connection the user just consented to.
  const onCancel = useCallback(() => history.push("/integrations"), [history]);

  const pageTitle = `Connect ${INTEGRATION_NAMES[provider]}`;
  const pageSubtitle = model?.username
    ? `Step 2 of 2 — signed in as ${model.username}. Pick the calendars to sync.`
    : "Step 2 of 2 — pick the calendars to sync.";

  const calendarsBody = isReadingCalendars ? (
    <LoadingIndicator />
  ) : (
    <CalendarPickerList
      calendars={calendars}
      selected={selected}
      onChange={onChange}
      onSelectAll={onSelectAll}
      primaryCalendarId={model?.username}
    />
  );

  return (
    <PageTemplate sidebarId="integrations" title={pageTitle} subtitle={pageSubtitle}>
      <div className={styles.page}>
        <Card>
          <Card.Header
            title="Calendars"
            subtitle={`Only ticked calendars sync into ${appConfig.displayName}. Everything else on this account stays private.`}
          />
          <Card.Body>
            <ApiError
              error={integration.updateError}
              defaultErrorMessage="Could not save your calendar selection. Please try again."
            />
            {calendarsBody}
          </Card.Body>
          <Card.Footer>
            <span>
              {selectedCount} of {calendars.length} selected
            </span>
            <div className={styles.footerActions}>
              <Button hierarchy="secondaryGray" onClick={onCancel}>
                Cancel
              </Button>
              <Button hierarchy="primary" onClick={onSave} disabled={integration.update}>
                Save and finish
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default IntegrationConnectCalendarsPage;
